import { ModalClose } from '@icons'
import cn from '@integrations/classnames'
import { type KeyboardEvent, type MouseEvent, type ReactNode, useEffect, useRef } from '@integrations/react'
import { useLocation } from '@integrations/routing'
import { tr, useModal } from 'mmfintech-commons'

import styles from './ModalDialog.module.scss'

type ModalDialogOptions = {
  closeOnClickOutside?: boolean
  closeOnEscape?: boolean
  hideCloseIcon?: boolean
  scrollable?: boolean
  maxWidth?: number
  onClose?: () => void
}

interface Props {
  content: ReactNode
  options?: ModalDialogOptions
  visible?: boolean
  onClose?: () => void
}

function ModalDialog({ content, options, visible, onClose }: Props) {
  const { ref, width, checkWidth, handleClose, overflowing, modalOptions, handleMouseDown } = useModal({
    options,
    visible,
    onClose
  })

  const location = useLocation()

  // A press that starts inside the popup is not an outside click. It is flagged here and left to bubble (also from
  // portaled menus and calendars, which bubble through the React tree), so document-level listeners such as an open
  // date picker's outside-click handler still see it.
  const pressedInside = useRef(false)

  function handleBackdropMouseDown(event: MouseEvent<HTMLDivElement>) {
    if (pressedInside.current) {
      pressedInside.current = false
      return
    }
    handleMouseDown(event)
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: close when path changes
  useEffect(() => {
    if (visible) {
      onClose?.()
    }
  }, [location.pathname])

  function handleEnter(event: KeyboardEvent<HTMLDivElement>) {
    if (
      event.key !== 'Enter' ||
      event.defaultPrevented ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.shiftKey
    ) {
      return
    }

    const target = event.target as HTMLElement

    // Preserve native keyboard behavior for controls that use Enter themselves. In particular,
    // Enter must remain a newline in textareas and must select options in open comboboxes.
    if (target.closest('textarea, button, a, [contenteditable="true"], [role="combobox"], [role="listbox"]')) {
      return
    }

    // Forms with a real submit control already submit on Enter. The fallback below covers older
    // modal forms and action dialogs whose primary MMButton is intentionally type="button".
    const form = target.closest('form')
    if (form?.querySelector('button[type="submit"]:not(:disabled), input[type="submit"]:not(:disabled)')) return

    // The modal's final primary button is its commit/send action. Earlier primary buttons can be
    // inline helpers (for example, "add message" inside the support composer).
    const primaryActions = event.currentTarget.querySelectorAll<HTMLButtonElement>(
      'button.mm-button-primary:not(:disabled)'
    )
    const primaryAction = primaryActions.item(primaryActions.length - 1)

    if (primaryAction) {
      event.preventDefault()
      primaryAction.click()
    }
  }

  return visible ? (
    <div
      onMouseDown={handleBackdropMouseDown}
      onKeyDown={handleEnter}
      className={cn(styles['modal-dialog-wrapper'], 'modal-dialog-wrapper', {
        [styles['overflowing']]: overflowing,
        overflowing
      })}
      ref={ref}
    >
      <div
        id='modal-container'
        className={cn(styles['modal-container'], 'modal-container', `icon-${modalOptions.closeIconPosition}`, {
          [styles['scrollable']]: modalOptions.scrollable,
          scrollable: modalOptions.scrollable,
          extended: width >= checkWidth
        })}
      >
        <div
          className={cn(
            styles['popup-main-content'],
            'popup-main-content',
            'modal-frame-surface',
            'raised-frame-surface'
          )}
          onMouseDown={() => {
            pressedInside.current = true
          }}
        >
          {content}
          {!modalOptions.hideCloseIcon && (
            <button
              type='button'
              className={cn(styles['popup-close-button-inline'], 'popup-close-button-inline', 'popup-close-button')}
              aria-label={tr('FRONTEND.BUTTONS.CLOSE', 'Close')}
              data-test='popup-close-button'
              onClick={handleClose}
            >
              <ModalClose />
            </button>
          )}
        </div>
      </div>
    </div>
  ) : null
}

export default ModalDialog
